/**
 * Multi-panel trader layout state management.
 *
 * This context manages:
 *   - Which trader instances are open (equity, fx, bonds, etf)
 *   - Their positions and sizes (for tiling/resizing)
 *   - Which panel has focus (activeTrader)
 *   - Layout mode (grid vs tabs)
 *   - Persistence to localStorage
 *   - Unique ID generation for each trader instance
 *
 * Usage:
 *   const { addTrader, removeTrader, setActiveTrader, ... } = useMultiTrader();
 *   const instance = useTraderInstance('trader-1');
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * A single trader panel instance.
 */
export interface TraderInstance {
  id: string;
  type: 'equity' | 'fx' | 'bonds' | 'etf';
  position?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  createdAt: number; // timestamp for ordering
}

/**
 * Complete state for the multi-panel layout.
 */
export interface MultiTraderState {
  traders: TraderInstance[];
  activeTrader: string | null;
  layoutMode: 'grid' | 'tabs';
}

/**
 * Action methods exposed via the context.
 */
export interface MultiTraderContextValue extends MultiTraderState {
  addTrader: (type: 'equity' | 'fx' | 'bonds' | 'etf') => string; // returns new ID
  removeTrader: (id: string) => void;
  setActiveTrader: (id: string | null) => void;
  updateTraderPosition: (
    id: string,
    position: { x: number; y: number; width: number; height: number }
  ) => void;
  setLayoutMode: (mode: 'grid' | 'tabs') => void;
  getTraderCount: () => number;
  getTrader: (id: string) => TraderInstance | undefined;
  clearAllTraders: () => void;
}

const MultiTraderCtx = createContext<MultiTraderContextValue | null>(null);

/**
 * Generate a unique ID for a trader instance.
 * Uses a timestamp-based counter to ensure uniqueness and ordering.
 */
function generateTraderID(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `trader-${timestamp}-${random}`;
}

/**
 * localStorage key for persisting state.
 */
const STORAGE_KEY = 'multitrader-state';

/**
 * Default state when no localStorage data exists.
 */
const DEFAULT_STATE: MultiTraderState = {
  traders: [],
  activeTrader: null,
  layoutMode: 'grid',
};

/**
 * Load state from localStorage, with fallback to defaults.
 */
function loadStateFromStorage(): MultiTraderState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_STATE;
    const parsed = JSON.parse(stored) as MultiTraderState;
    // Validate structure
    if (
      Array.isArray(parsed.traders)
      && (parsed.activeTrader === null || typeof parsed.activeTrader === 'string')
      && (parsed.layoutMode === 'grid' || parsed.layoutMode === 'tabs')
    ) {
      return parsed;
    }
  } catch {
    // Corrupt or missing — fall back to defaults
  }
  return DEFAULT_STATE;
}

/**
 * Save state to localStorage.
 */
function saveStateToStorage(state: MultiTraderState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage full or disabled — silently fail
  }
}

/**
 * Provider component. Wrap your app with this.
 */
export function MultiTraderProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<MultiTraderState>(() => loadStateFromStorage());

  // Whenever state changes, persist to localStorage
  useEffect(() => {
    saveStateToStorage(state);
  }, [state]);

  const addTrader = useCallback(
    (type: 'equity' | 'fx' | 'bonds' | 'etf'): string => {
      const id = generateTraderID();
      const newInstance: TraderInstance = {
        id,
        type,
        createdAt: Date.now(),
        // position will be set by consumer or layout engine
      };
      setState((prev) => ({
        ...prev,
        traders: [...prev.traders, newInstance],
        activeTrader: id, // new trader becomes active by default
      }));
      return id;
    },
    []
  );

  const removeTrader = useCallback((id: string) => {
    setState((prev) => {
      const newTraders = prev.traders.filter((t) => t.id !== id);
      const newActive =
        prev.activeTrader === id
          ? newTraders[newTraders.length - 1]?.id ?? null
          : prev.activeTrader;
      return {
        ...prev,
        traders: newTraders,
        activeTrader: newActive,
      };
    });
  }, []);

  const setActiveTrader = useCallback((id: string | null) => {
    setState((prev) => {
      // If id is provided, verify it exists
      if (id && !prev.traders.some((t) => t.id === id)) {
        return prev;
      }
      return { ...prev, activeTrader: id };
    });
  }, []);

  const updateTraderPosition = useCallback(
    (id: string, position: { x: number; y: number; width: number; height: number }) => {
      setState((prev) => ({
        ...prev,
        traders: prev.traders.map((t) =>
          t.id === id ? { ...t, position } : t
        ),
      }));
    },
    []
  );

  const setLayoutMode = useCallback((mode: 'grid' | 'tabs') => {
    setState((prev) => ({ ...prev, layoutMode: mode }));
  }, []);

  const getTraderCount = useCallback(() => state.traders.length, [state.traders.length]);

  const getTrader = useCallback(
    (id: string) => state.traders.find((t) => t.id === id),
    [state.traders]
  );

  const clearAllTraders = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  const value: MultiTraderContextValue = useMemo(
    () => ({
      ...state,
      addTrader,
      removeTrader,
      setActiveTrader,
      updateTraderPosition,
      setLayoutMode,
      getTraderCount,
      getTrader,
      clearAllTraders,
    }),
    [
      state,
      addTrader,
      removeTrader,
      setActiveTrader,
      updateTraderPosition,
      setLayoutMode,
      getTraderCount,
      getTrader,
      clearAllTraders,
    ]
  );

  return (
    <MultiTraderCtx.Provider value={value}>
      {children}
    </MultiTraderCtx.Provider>
  );
}

/**
 * Hook: get the full multi-trader context and all methods.
 */
export function useMultiTrader(): MultiTraderContextValue {
  const ctx = useContext(MultiTraderCtx);
  if (!ctx) {
    throw new Error('useMultiTrader outside MultiTraderProvider');
  }
  return ctx;
}

/**
 * Hook: get a specific trader instance by ID, with reactive updates.
 */
export function useTraderInstance(id: string): TraderInstance | undefined {
  const { getTrader } = useMultiTrader();
  const [instance, setInstance] = useState<TraderInstance | undefined>(() =>
    getTrader(id)
  );

  useEffect(() => {
    setInstance(getTrader(id));
  }, [id, getTrader]);

  return instance;
}

/**
 * Hook: get the currently active trader instance.
 */
export function useActiveTrader(): TraderInstance | undefined {
  const { activeTrader, getTrader } = useMultiTrader();
  const [instance, setInstance] = useState<TraderInstance | undefined>(() =>
    activeTrader ? getTrader(activeTrader) : undefined
  );

  useEffect(() => {
    setInstance(activeTrader ? getTrader(activeTrader) : undefined);
  }, [activeTrader, getTrader]);

  return instance;
}

/**
 * Hook: get all traders of a specific type.
 */
export function useTradersByType(
  type: 'equity' | 'fx' | 'bonds' | 'etf'
): TraderInstance[] {
  const { traders } = useMultiTrader();
  return useMemo(() => traders.filter((t) => t.type === type), [traders, type]);
}
